import { classify } from 'sidecar-utilities'
import { Sentiment } from 'sidecar-components/types'

export const globalNavStyles = ({
  hasMainNavItems,
  sentiment = Sentiment.DEFAULT,
}: {
  hasMainNavItems?: boolean
  sentiment?: Sentiment.DEFAULT | Sentiment.PRIMARY
}) =>
  classify({
    layout: 'flex justify-between items-center',
    typography: 'sh-body',
    colors:
      // login page's header is blue to mirror its all blue page
      sentiment === Sentiment.PRIMARY
        ? 'color-bgPrimary color-borderPrimary'
        : 'color-bgDefaultContainer color-border-weak-default',
    // if there are no nav items, the top border is not needed
    mobileBorder: hasMainNavItems ? 'max-md:border-t' : '',
    desktopBorder: 'md:border-b',
    printBorder: 'print:!border-t-0',
    desktopPadding: 'md:py-4 md:px-6',
    printPadding: 'print:pb-4',
    // iPhones have a bar at the bottom of their screen that overlaps the app on PWAs.
    // This pushes the nav up.
    // See more at https://webkit.org/blog/7929/designing-websites-for-iphone-x/.
    // If there are no items, there's no need to add the padding.
    iOSPadding: hasMainNavItems ? 'pb-[var(--safe-bottom)]' : '',
  })

export const logoLinkStyles = (sentiment: Sentiment = Sentiment.DEFAULT) =>
  classify({
    iconColors:
      sentiment === Sentiment.PRIMARY
        ? '[&_svg]:color-iconOnPrimary'
        : '[&_svg]:color-logo',
    focusOutline:
      'focus:outline focus:outline-offset-4 focus:outline-2 focus:color-outlinePrimaryDecor rounded-md',
    separateFromNav: 'md:mr-6',
  })

export const mainNavStyles = classify({
  layout: 'flex',
  specLayout: 'p-1 md:p-0 gap-px md:gap-1 w-full',
  hideOnPrint: 'print:hidden',
})

// flex-1 makes each link full width
export const linkWrapperStyles = 'relative flex max-md:flex-1'

// these styles are used for every button in the main navigation
const buttonAndLinkStyles = (sentiment: Sentiment = Sentiment.DEFAULT) => ({
  bgColor:
    sentiment === Sentiment.PRIMARY
      ? `color-bgPrimary
        hover:color-bgPrimary-hover 
        active:color-bgPrimary-active 
        focus:color-bgPrimary-hover`
      : `color-bgDefaultContainer
        hover:color-bgDefaultContainer-hover 
        active:color-bgDefaultContainer-active 
        focus:color-bgDefaultContainer-hover`,
  textColor:
    sentiment === Sentiment.PRIMARY
      ? 'max-md:color-textOnPrimary md:color-textOnPrimary hover:color-textOnPrimary-hover active:color-textOnPrimary-active focus:color-textOnPrimary-hover'
      : '',
  underline: '!no-underline',
  padding: 'px-3',
  specType: 'max-md:sh-body-2xs',
  layout: 'flex flex-col flex-grow justify-center items-center',
  specLayout: 'py-1 gap-1',
  mediumScreens: 'md:flex md:flex-row md:px-6 md:py-2 md:gap-3',
  extras: 'whitespace-nowrap rounded-lg cursor-pointer',
  // pushing this a far to the front as I can because on iOS
})

// This style is actually a function so that we can pass in 'selected' as an
// arg and have it apply the style to just the one link that should display
// as selected.
export const linkStyles = ({
  isSelected,
  sentiment = Sentiment.DEFAULT,
}: {
  isSelected?: boolean
  sentiment?: Sentiment
}) => {
  const linkSpecificStyles = {
    defaultTextColor:
      sentiment === Sentiment.PRIMARY
        ? ''
        : // We have to add the "md:" to override the default link color
          'md:color-textOnDefault',
  }

  const mobileTextColor =
    sentiment === Sentiment.PRIMARY
      ? 'max-md:color-textOnPrimary'
      : 'max-md:color-textPrimary'

  const selectedMobileTextColor =
    sentiment === Sentiment.PRIMARY
      ? 'max-md:color-textOnPrimary'
      : 'max-md:color-textOnDefaultWeak'

  const selectedBorderColor =
    sentiment === Sentiment.PRIMARY
      ? 'md:color-borderOnPrimary'
      : 'md:color-borderDefaultDecorStrong'

  const selectedLinkStyles = isSelected
    ? {
        font: 'md:sh-strong',
        mobileTextColor: mobileTextColor,
        borderColor: selectedBorderColor,
        border: 'md:border-b-4 md:rounded-b-none',
      }
    : {
        mobileTextColor: selectedMobileTextColor,
      }
  return classify({
    // The link and button look and act the same. It's just that one is navigational and the other opens a menu.
    // Add both the styles needed for both the link and button,
    // as well as add the styles specific to the link
    ...linkSpecificStyles,
    ...selectedLinkStyles,
    ...buttonAndLinkStyles(sentiment),
  })
}

export const buttonStyles = ({
  isSelected,
  sentiment = Sentiment.DEFAULT,
}: {
  isSelected?: boolean
  sentiment?: Sentiment
}) => {
  const buttonSpecificStyles = {
    fontStates:
      sentiment === Sentiment.PRIMARY
        ? ''
        : // links get these styles out of the box. We have to add them here for buttons
          'hover:color-textPrimary-hover active:color-textPrimary-active focus:color-textPrimary-hover',
    // Add the same group identifier as a default Link. Used for the icon to pick up button's state
    group: 'group/link',
  }
  const selectedBtnStyles = isSelected
    ? {
        fontColor: sentiment === Sentiment.PRIMARY ? '' : 'color-textPrimary',
        font: 'md:sh-strong',
        border:
          'md:color-borderDefaultDecorStrong md:border-b-4 md:rounded-b-none',
      }
    : {
        fontColor:
          sentiment === Sentiment.PRIMARY
            ? ''
            : 'color-textOnDefaultWeak md:color-textOnDefault',
      }
  return classify({
    // The link and button look and act the same. It's just that one is navigational and the other opens a menu.
    // Add both the styles needed for both the link and button,
    // as well as add the styles specific to the button
    ...buttonSpecificStyles,
    ...selectedBtnStyles,
    ...buttonAndLinkStyles(sentiment),
  })
}

export const mobileIconStyle = ({
  isSelected,
  sentiment = Sentiment.DEFAULT,
}: {
  isSelected?: boolean
  sentiment?: Sentiment
}) => {
  const defaultIconColor =
    sentiment === Sentiment.PRIMARY
      ? `color-iconOnPrimary
          group-hover/link:color-iconOnPrimary-hover 
          group-active/link:color-iconOnPrimary-active
          group-focus/link:color-iconOnPrimary-hover`
      : `color-icon-default 
          group-hover/link:color-iconPrimary-hover 
          group-active/link:color-iconPrimary-active
          group-focus/link:color-iconPrimary-hover`

  const selectedIconColor =
    sentiment === Sentiment.PRIMARY
      ? 'color-iconOnPrimary'
      : 'color-iconPrimary'

  return classify({
    visibility: 'md:hidden',
    iconColor: isSelected ? selectedIconColor : defaultIconColor,
  })
}

export const chevronStyle = (sentiment: Sentiment = Sentiment.DEFAULT) =>
  classify({
    base: 'w-4 h-4 hidden',
    color:
      sentiment === Sentiment.PRIMARY
        ? 'color-iconOnPrimary'
        : 'color-icon-default',
    effects:
      sentiment === Sentiment.PRIMARY
        ? 'group-hover/link:color-iconOnPrimary-hover group-active/link:color-iconOnPrimary-active group-focus/link:color-iconOnPrimary-hover'
        : 'group-hover/link:color-iconPrimary-hover group-active/link:color-iconPrimary-active group-focus/link:color-iconPrimary-hover',
    mobile: 'md:flex',
  })

export const printContactOption =
  'flex gap-1 items-center sh-body-xs mb-1 last:mb-0'
